<script setup lang="ts">
import $ from 'jquery';
import { nextTick, onMounted, ref, shallowRef, unref, watch } from 'vue';

import { useDropup, useJqueryEventListener } from '#public_ts/composables';

const props = defineProps<{
    icon?: string;
    label?: string | boolean;
    title?: string;
    buttonClass?: string;
    menuClass?: string;
    showDropdown?: boolean;
    freezeDropdown?: boolean;
    autoDropUp?: boolean;
    unloadCollapsed?: boolean;
}>();
//eslint-obsolete no-spaced-func
const emit = defineEmits<{
    (event: 'update:showDropdown', value: boolean | undefined): void;
    (event: 'dropdown-opened', value: undefined): void;
    (event: 'dropdown-closed', value: undefined): void;
}>();

const localShowDropdown = ref<boolean>(false);
const isDestroyCollapsed = ref<boolean>(!unref(localShowDropdown));
const transition = ref(false);
const dropdownElement = shallowRef<HTMLElement | undefined>();
const dropdownToggle = shallowRef<HTMLElement | undefined>();
const dropdownMenu = shallowRef<HTMLElement | undefined>();
let isDropup = ref(false);
let evaluateDropUp = () => {};
const dropdownHeight = ref(-1);
if(props.autoDropUp) {
    ({ isDropup, evaluate: evaluateDropUp } = useDropup({ triggerRef: dropdownElement, dropdownHeight, isManualEvaluate: true }));
}
const dropDownClassFix = ref(false);

function waitCurrentTransition() {
    return new Promise((resolve) => {
        if(!unref(transition)) {
            resolve(undefined);
            return;
        }
        const _unwatch = watch(transition, () => {
            // multi-entry support, transition may be modified by other waiter
            // then we wait another transition
            if(unref(transition)) return;
            _unwatch();
            resolve(undefined);
        });
    });
}

function updateShowDropdown(_showDropdown: boolean | undefined) {
    const _showDropdown0 = !!_showDropdown;
    const _dropdownElement = unref(dropdownElement);
    const _dropdownToggle = unref(dropdownToggle);
    if(!_dropdownElement || !_dropdownToggle) return Promise.resolve();

    return Promise.resolve()
        .then(() => waitCurrentTransition())
        .then(() => {
            if(_showDropdown0 === unref(localShowDropdown)) return;
            if(checkProcessFrozen()) return;

            updateMenuHeight();
            transition.value = true;
            localShowDropdown.value = _showDropdown0;
            $(_dropdownToggle).dropdown('toggle');
        });
}

function processDropdownToggle(_showDropdown: boolean | undefined) {
    const _showDropdown0 = !!_showDropdown;
    nextTick(() => {
        if(_showDropdown0 !== unref(localShowDropdown)) {
            // warning, properties not in sync, see updateShowModal
            localShowDropdown.value = _showDropdown0;
        }
        if(!!props.showDropdown !== _showDropdown0) {
            emit('update:showDropdown', _showDropdown0);
        }
        transition.value = false;
        if(_showDropdown0) {
            emit('dropdown-opened', undefined);
        } else {
            emit('dropdown-closed', undefined);
        }
    });
}

function checkProcessFrozen() {
    if(props.freezeDropdown) {
        const _showDropdown0 = !!unref(localShowDropdown);
        if(!!props.showDropdown !== _showDropdown0) {
            emit('update:showDropdown', _showDropdown0);
        }
        return true;
    }
    return false;
}

function getHiddenHeight(el: HTMLElement) {
    const prev = el.style.cssText;
    el.style.cssText = 'position:absolute; visibility:hidden; display:block !important';
    const height = el.offsetHeight;
    el.style.cssText = prev;
    return height;
}

function updateMenuHeight() {
    const _dropdownMenu = unref(dropdownMenu);
    if(props.autoDropUp && _dropdownMenu) {
        dropdownHeight.value = getHiddenHeight(_dropdownMenu) + 20;
        evaluateDropUp();
    }
}

watch(() => props.showDropdown, (val, old) => {
    if(!!val !== !!old) {
        updateShowDropdown(val);
    }
});

onMounted(() => {
    //portal requires next tick
    nextTick(() => {
        updateMenuHeight();
        if(props.showDropdown) {
            updateShowDropdown(true);
        }
    });
});

useJqueryEventListener(dropdownElement, 'show.bs.dropdown', (evt) => {
    if(evt && props.freezeDropdown) {
        evt.preventDefault();
        return false;
    }

    updateMenuHeight();
    transition.value = true;
    isDestroyCollapsed.value = false;
    return undefined;
});
useJqueryEventListener(dropdownElement, 'shown.bs.dropdown', () => {
    processDropdownToggle(true);
    dropDownClassFix.value = true;
});
useJqueryEventListener(dropdownElement, 'hide.bs.dropdown', (evt) => {
    if(evt && props.freezeDropdown) {
        evt.preventDefault();
        return false;
    }
    transition.value = true;
    return undefined;
});
useJqueryEventListener(dropdownElement, 'hidden.bs.dropdown', () => {
    processDropdownToggle(false);
    dropDownClassFix.value = false;
    isDestroyCollapsed.value = true;
});
</script>
<template>
    <div ref="dropdownElement" class="btn-group" :class="{ 'dropup': isDropup, 'open': dropDownClassFix }">
        <slot name="button" />
        <button
            ref="dropdownToggle"
            type="button"
            class="btn dropdown-toggle"
            :class="props.buttonClass || 'btn-default'"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            :title="props.title"
        >
            <slot name="dropdown">
                <span v-if="props.icon" class="bi" :class="props.icon" /> {{ props.label || '' }}
            </slot>
            <slot name="caret">
                <span class="caret" />
            </slot>
        </button>
        <ul
            v-if="!props.unloadCollapsed || !isDestroyCollapsed"
            ref="dropdownMenu"
            class="dropdown-menu"
            :class="props.menuClass ?? ''"
            role="menu"
        >
            <slot />
        </ul>
    </div>
</template>
